import { ComponentKey } from 'enums/componentKey'
import * as Yup from 'yup'
import {
  DocumentProcessDialogConfig,
  DocumentProcessFormConfig,
  ExtractionResult
} from '../types'
import { BankDetailsExtractionResult } from 'api/documents'
import { requiredIbanSchema } from 'shared/utils/ibanSchema'

// Re-export for convenience
export type { BankDetailsExtractionResult }

/**
 * Form values for RIB bank account creation
 */
export interface RibFormValues extends Record<string, unknown> {
  iban: string
  bic: string
  label: string
}

/**
 * Form configuration for RIB upload
 */
export const ribFormConfig: DocumentProcessFormConfig<RibFormValues> = {
  fields: [
    {
      name: 'iban',
      componentKey: ComponentKey.Iban,
      label: 'IBAN',
      placeholder: 'FR76 3000 1007 9412 3456 7890 185',
      required: true,
      colSpan: 6
    },
    {
      name: 'bic',
      componentKey: ComponentKey.InputText,
      label: 'BIC / SWIFT',
      placeholder: 'BNPAFRPP',
      required: false,
      type: 'text',
      colSpan: 3
    },
    {
      name: 'label',
      componentKey: ComponentKey.InputText,
      label: 'Libellé du compte',
      placeholder: 'Compte principal',
      required: true,
      type: 'text',
      colSpan: 3
    }
  ],
  validationSchema: Yup.object().shape({
    iban: requiredIbanSchema,
    bic: Yup.string()
      .min(8, 'Le BIC doit contenir au moins 8 caractères')
      .max(11, 'Le BIC ne peut pas dépasser 11 caractères')
      .nullable(),
    label: Yup.string()
      .required('Le libellé est requis')
      .max(100, 'Le libellé ne peut pas dépasser 100 caractères')
  }) as Yup.ObjectSchema<RibFormValues>,
  initialValues: {
    iban: '',
    bic: '',
    label: ''
  }
}

/**
 * Mapper to convert extraction result to form values
 */
export const ribExtractionMapper = (
  extracted: BankDetailsExtractionResult
): Partial<RibFormValues> => ({
  iban: extracted.iban || '',
  bic: extracted.bic || ''
})

/**
 * Creates a RIB upload dialog configuration
 */
export const createRibUploadConfig = (options: {
  personId: string
  onExtractIban: (storedFileId: string) => Promise<BankDetailsExtractionResult>
  onCreateBankAccount: (values: RibFormValues, storedFileId: string, personId: string) => Promise<{ success: boolean; errorMessage?: string }>
  onSuccess?: () => void
  onCancel?: () => void
}): DocumentProcessDialogConfig<RibFormValues, BankDetailsExtractionResult> => ({
  title: 'Ajouter un compte bancaire',
  subtitle: 'Uploadez votre RIB pour créer un nouveau compte bancaire',

  acceptedFormats: '.pdf,.jpg,.jpeg,.png',
  uploadLabel: 'Glissez-déposez votre RIB ici',
  maxFileSizeMB: 10,

  formConfig: ribFormConfig,

  extraction: {
    onExtract: async (storedFileId: string): Promise<ExtractionResult<BankDetailsExtractionResult>> => {
      try {
        const result = await options.onExtractIban(storedFileId)
        return {
          success: result.isSuccess ?? false,
          data: result,
          errorMessage: result.errorMessage ?? undefined
        }
      } catch (error) {
        return {
          success: false,
          errorMessage: 'Erreur lors de l\'extraction de l\'IBAN'
        }
      }
    },
    mapper: ribExtractionMapper,
    loadingMessage: 'Extraction de l\'IBAN en cours...'
  },

  submit: {
    onSubmit: async (values: RibFormValues, storedFileId: string) => {
      return options.onCreateBankAccount(values, storedFileId, options.personId)
    },
    submitLabel: 'Créer le compte',
    successMessage: 'Compte bancaire créé avec succès'
  },

  onSuccess: options.onSuccess,
  onCancel: options.onCancel
})
